import { useState, useRef } from 'react';
import { withStyles, Card } from 'vopay';
import styles from './CreateCustomerForm.module.scss';
import { Title, Button, UtilButton } from 'UI';
import { useDialog } from '@/providers/Dialog.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';
import usePostIQ11Tokenize from '@/hooks/useApi/usePostIQ11Tokenize';
import { useClientData } from '@/providers/ClientData.provider';
import { useTranslation } from 'react-i18next';
import usePostEmbedUrl from '@/hooks/useApi/usePostEmbedUrl';
import usePostSendPaylink from '@/hooks/useApi/usePostSendPaylink';
import DialogLink from './DialogLink';
import ElinxConnect from '@/components/ElinxConnect/ElinxConnect';
import { useToast } from '@/providers/Toast.provider';
import usePaymentMethods from '@/hooks/usePaymentMethods';
import useFormConfig, { SerializedFormValues, MoreInfoConfigProps } from './useFormConfig';

const cn = withStyles(styles);

type AccountID = string;

type CreateCustomerFormProps<T extends object> = {
  customerLabel: string;
  // not an event per se, as it is communicating directly with its parent, hence why the prop doesn't start with "on"
  createAccount: (values: SerializedFormValues & T) => Promise<AccountID>;
  onComplete: (
    accountID: string,
    search?: {
      cardTab: string;
      activityType: string;
    },
  ) => void;
} & MoreInfoConfigProps<T>;

export default function CreateCustomerForm<T extends object>({
  onComplete,
  customerLabel,
  createAccount,
  moreInfoConfig: moreInfoConfigProp = [],
  moreInfoInitialValues = {} as T,
}: CreateCustomerFormProps<T>) {
  const {
    response: { LoginUserName, clientAccountsEnabled, Country: loginUserCountry, AccountName },
  } = useClientData();

  const dialog = useDialog();
  const CUSTOMER_ID_PROP = clientAccountsEnabled ? 'ClientAccountID' : 'ContactID';
  const alertDialog = useAlertDialog();
  const [, setPending] = useDataFetchIndicator();
  const [reviewing, setReviewing] = useState(false);
  const [, , addBankAccount] = usePostIQ11Tokenize({});
  const {
    ctx,
    renderFormElements,
    infoCollectionMethodConfig,
    accountTypeConfig,
    baseConfig,
    moreInfoConfig,
    primaryContactConfig,
    addressConfig,
    connectionTypeConfig,
    paymentMethodConfig,
  } = useFormConfig({
    customerLabel,
    reviewing,
    onSubmit: handleSubmit,
    moreInfoConfig: moreInfoConfigProp,
    moreInfoInitialValues,
  });

  const formRef = useRef<HTMLFormElement | null>(null);
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.Create' });
  const [, , , generateEmbedUrl] = usePostEmbedUrl({});
  const [, , , sendPaylink] = usePostSendPaylink({});
  const toast = useToast();
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);
  const { setDefaultMethod, addInterac } = usePaymentMethods();
  const [showingMore, setShowingMore] = useState(false);

  async function handleSubmit(values: SerializedFormValues & T) {
    try {
      setPending(true);
      const accountID = await createAccount(values);

      toast.success(
        t('accountCreated', {
          label: customerLabel,
        }),
      );

      if (values.connectionType === 'none') {
        onComplete(accountID);
      } else {
        await handleAddPaymentMethod(values, accountID);
      }
    } catch (err) {
      if (err instanceof Error) {
        alertDialog.error('Error', err.message);
      }
    } finally {
      setPending(false);
    }
  }

  async function handleAddPaymentMethod(values: SerializedFormValues, accountID: string) {
    if (values.connectionType === 'provide') {
      if (values.providedPaymentMethod === 'bank') {
        await handleAddBankAccount(values, accountID);
      } else if (values.providedPaymentMethod === 'interac') {
        await handleAddInterac(values, accountID);
      } else {
        setPending(false);
        await handleAddCard(values, accountID);
      }

      onComplete(accountID);
    } else if (values.connectionType === 'request') {
      setPending(false);
      await handleRequestPaymentMethod(values, accountID);
    }
  }

  async function handleAddInterac(values: SerializedFormValues, accountID: string) {
    try {
      const res = await addInterac({
        accountID,
        recipientName:
          values.accountType === 'business' ? values.businessName : `${values.firstName} ${values.lastName}`,
        email: values.interacEmail,
        phone: values.interacPhone,
        question: values.interacQuestion,
        answer: values.interacAnswer,
      });

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      const setDefaultRes = await setDefaultMethod(
        values.interacDeliveryMethod === 'email' ? 'Email' : 'PhoneNumber',
        res.Token,
        accountID,
      );

      if (!setDefaultRes.Success) {
        throw new Error(setDefaultRes.ErrorMessage);
      }
    } catch (err) {
      console.error(err);

      if (err instanceof Error) {
        alertDialog.error('Error', err.message, {
          abortLabel: t('close'),
        });
      }
    }
  }

  async function handleRequestPaymentMethod(values: SerializedFormValues, accountID: string) {
    try {
      setPending(true);

      const embedUrlRes = await generateEmbedUrl({
        RedirectURL: 'https://request-dev.vopay.com/',
        Language: 'en',
        RedirectMethod: 'JavascriptMessage',
        PaymentSelectionMethod: values.requestedPaymentMethods,
        AccountSelectionMethod: values.accountSelectionMethods,
        ClientControlled: clientAccountsEnabled,
        [CUSTOMER_ID_PROP]: accountID,
        ClientReferenceNumber: '',
        TermsAndConditions: '',
        Country: loginUserCountry,
        Version: 'v2',
      });

      if (!embedUrlRes.Success) {
        throw new Error(embedUrlRes.ErrorMessage);
      }

      const receiverName =
        values.accountType === 'business' ? values.businessName : `${values.firstName} ${values.lastName}`;

      const sendPaylinkRes = await sendPaylink({
        SenderName: AccountName,
        ReceiverName: receiverName,
        ReceiverEmailAddress: values.email,
        ReceiverPhoneNumber: values.phone,
        IframeKey: embedUrlRes.IframeKey,
        [CUSTOMER_ID_PROP]: accountID,
        SendNotification: !!values.notificationMethod,
        NotificationMethod: values.notificationMethod,
      });

      if (!sendPaylinkRes.Success) {
        throw new Error(sendPaylinkRes.ErrorMessage);
      }

      setPending(false);

      const successChoice = await alertDialog.success(
        t('elinxSent'),
        <div className={cn('sendRequestSuccessDetails')}>
          {t('elinxSentDetails', { receiverName })}
          <DialogLink label="eLinx Embedded Link" link={sendPaylinkRes.Link} />
        </div>,
        {
          abortLabel: t('close'),
          confirmLabel: t('viewRequestDetails'),
        },
      );

      if (successChoice === 'confirm') {
        onComplete(accountID, {
          cardTab: 'activity',
          activityType: 'eLinx History',
        });
      } else {
        onComplete(accountID);
      }
    } catch (err) {
      if (err instanceof Error) {
        alertDialog.error('Error', err.message, {
          abortLabel: t('close'),
        });
      }
    } finally {
      setPending(false);
    }
  }

  async function handleAddBankAccount(values: SerializedFormValues, accountID: string) {
    const bankRes = await addBankAccount({
      PortalUser: LoginUserName,
      AccountHolderName:
        values.accountType === 'business' ? values.businessName : `${values.firstName} ${values.lastName}`,
      Currency: values.bankCurrency || (loginUserCountry === 'CA' ? 'CAD' : 'USD'),
      AccountNumber: values.accountNum,
      FinancialInstitutionNumber: values.fiID,
      BranchTransitNumber: values.branchTransitNo,
      ABARoutingNumber: values.ABARoutingNumber, // for US account
      ClientControlled: clientAccountsEnabled,
      [CUSTOMER_ID_PROP]: accountID,
      Address1: values.address1,
      City: values.city,
      Province: values.province,
      PostalCode: values.postalCode,
      PhoneNumber: values.phone,
      Country: loginUserCountry,
    });

    if (!bankRes.Success) {
      await alertDialog.error(t('error'), t('addBankAccountError'), {
        confirmLabel: t('proceed'),
      });
    }
  }

  async function handleAddCard(values: SerializedFormValues, accountID: string) {
    const token = await dialog.open<string>(({ onCancel, onConfirm }) => (
      <Card className={cn('elinxConnectDialog')}>
        <ElinxConnect
          onClose={onCancel}
          onSuccess={(args) => {
            onConfirm(args.data.Token);
          }}
          setting={{
            RedirectURL: 'https://vopay.com',
            Version: 'v2',
            Country: loginUserCountry,
            PaymentSelectionMethod: values.providedPaymentMethod,
            ClientControlled: clientAccountsEnabled,
            [CUSTOMER_ID_PROP]: accountID,
            RedirectMethod: 'JavascriptMessage',
          }}
          loadingMessage={<h2 className={cn('elinxLoadingMessage')}>{t('pleaseWait')}</h2>}
        />
      </Card>
    ));

    if (token) {
      await setDefaultMethod(values.providedPaymentMethod === 'credit' ? 'CreditCard' : 'DebitCard', token, accountID);
    }
  }

  return (
    <form ref={formRef} onSubmit={ctx.handleSubmit} className={cn('createCustomerForm')}>
      <Card className={cn('card')}>
        <div className={cn('paymentMethodCardHeader')}>
          <Title level="5">{t('customerInformation', { label: customerLabel })}</Title>
          {renderFormElements({ config: infoCollectionMethodConfig })}
        </div>

        <div>{renderFormElements({ config: accountTypeConfig })}</div>
        <div className={cn('grid')}>
          {renderFormElements({ config: baseConfig })}

          {showingMore &&
            ctx.values.infoCollectionMethod === 'manual' &&
            renderFormElements({ config: moreInfoConfig })}
        </div>

        {!showingMore && ctx.values.infoCollectionMethod === 'manual' && (
          <UtilButton size="large" iconLeft="add" onClick={() => setShowingMore(true)} disabled={reviewing}>
            {t('additionalInformation')}
          </UtilButton>
        )}
      </Card>

      {ctx.values.infoCollectionMethod === 'manual' && ctx.values.accountType === 'business' && (
        <Card className={cn('card')}>
          <Title level="5">{t('primaryContactInformation')}</Title>
          <div className={cn('grid')}>{renderFormElements({ config: primaryContactConfig })}</div>
        </Card>
      )}

      {ctx.values.infoCollectionMethod === 'manual' && (
        <Card className={cn('card')}>
          <Title level="5">{t('address')}</Title>
          <div className={cn('grid')}>{renderFormElements({ config: addressConfig })}</div>
        </Card>
      )}

      {ctx.values.infoCollectionMethod === 'manual' && (
        <Card className={cn('card')}>
          <div className={cn('paymentMethodCardHeader')}>
            <Title level="5">{t('paymentMethod')}</Title>
            {renderFormElements({ config: connectionTypeConfig })}
          </div>

          <div className={cn('grid')}>{renderFormElements({ config: paymentMethodConfig })}</div>
        </Card>
      )}

      {reviewing ? (
        <div className={cn('reviewCtas')}>
          <Button
            iconLeft="edit"
            onClick={() => {
              formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              setReviewing(false);
            }}
            style={{
              width: submitButtonRef.current?.offsetWidth,
            }}
          >
            {t('editButton')}
          </Button>

          <Button ref={submitButtonRef} type="submit" colorScheme="primary" disabled={!ctx.isValid}>
            {t('submitButton')}
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          colorScheme="primary"
          disabled={!ctx.isValid}
          onClick={() => {
            formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setReviewing(true);
          }}
          className={cn('reviewButton')}
        >
          {t('reviewButton')}
        </Button>
      )}
    </form>
  );
}
